/**
 * 2-D Slopes plot used inside the Normalisation Contour subsection of CdA Normalisation
 * Summary. Renders one of the two slope plots (yaw or airspeed) with a scope-aware scatter
 * overlay driven by the parent's `dataLevel` radio button: traverse / run / config / all.
 *
 * Created as a fresh component (rather than reusing the shared LinePlot) so upcoming
 * markers / scope-specific styling / interactions can evolve here without touching
 * LinePlot's other consumers.
 */

import { ReactElement, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { LINE_COLOR, PALETTE } from '../SessionCharts/plotConstants';
import {
  useUpdateSession,
  useSetSession,
  useInvalidateSession,
} from '../../../../hooks/useSessions';

type PlotType = 'yaw_plot' | 'speed_plot' | 'head_plot' | 'chest_plot';
type DataLevel = 'traverse' | 'run' | 'config' | 'all';

// Channel keys in the per-traverse data, matching the backend slope plot's axes.
// X channels mirror NormalisationContour.tsx:24-25 and session_plotting.py:148/164/185/195.
// Y channels follow the convention: the channel that has the *other* axis's
// normalisation removed, so the swept axis's slope is visible in the residual.
// (e.g. when sweeping yaw, plot CdAXWnNAS_xG3 — air-speed normalisation removed.)
// For body position plots, the "other axis" is the other body channel:
// when sweeping head, plot CdAXWnNC_xG3 (chest normalisation removed); when
// sweeping chest, plot CdAXWnNH_xG3 (head normalisation removed). These are the
// per-traverse equivalents of CdA_DM_NC / CdA_DM_NH used by session_plotting.py:179,189.
const X_CHANNEL: Record<PlotType, string> = {
  yaw_plot: 'AirYawAbs_AG3',
  speed_plot: 'AirSpeed_vG3',
  head_plot: 'BodyPosHead_sG3_cm',
  chest_plot: 'BodyPosChest_sG3_cm',
};
const Y_CHANNEL: Record<PlotType, string> = {
  yaw_plot: 'CdAXWnNAS_xG3',
  speed_plot: 'CdAXWnNX_xG3',
  head_plot: 'CdAXWnNC_xG3',
  chest_plot: 'CdAXWnNH_xG3',
};

// Channel pair used to compute ΔCdA for the displayed text and the
// vertical connector / traverse-curve y-offset. Defined per the model:
//   yaw plot:    Δ = CdAN_xG3 - CdAXWnNX_xG3
//   speed plot:  Δ = CdAN_xG3 - CdAXWnNAS_xG3
//   head plot:   Δ = CdAN_xG3 - CdAXWnNH_xG3
//   chest plot:  Δ = CdAN_xG3 - CdAXWnNC_xG3
// CdAN_xG3 is the fully-normalised CdA; the subtrahend leaves the relevant
// axis's normalisation contribution un-applied. The difference is the
// component of normalisation attributable to that axis at this traverse.
const DELTA_MINUEND = 'CdAN_xG3';
const DELTA_SUBTRAHEND: Record<PlotType, string> = {
  yaw_plot: 'CdAXWnNX_xG3',
  speed_plot: 'CdAXWnNAS_xG3',
  head_plot: 'CdAXWnNH_xG3',
  chest_plot: 'CdAXWnNC_xG3',
};

interface Props {
  plotType: PlotType;
  dataLevel: DataLevel;
  session: any;
  // The plotData under session.plot_data[plotType][normType] (parent already resolves normType).
  plotData: any;
  // Config containing the selected run; used for the 'config' scope (per-config tuple lookup).
  configIdx: number;
  // Selected run object; used for 'run' / 'traverse' scopes (raw traverse arrays live here).
  currentRun: any | null;
  selectedTraverse: number;
  // Full configs list; passed through to keep the legend mirroring LinePlot's behaviour.
  configs: any[];
  // Reference X-coordinate on the slope (angleRef for yaw, speedRef for speed). The
  // component projects this onto the slope curve and renders a black marker there.
  xRef: number;
  // 'standard' or 'flattened' (the parent already resolves which scopes show flattened).
  // In flattened mode + non-Config-A traverses, the per-traverse y-values are shifted by
  // flattenedOffset so the red traverse marker (and the grey curve passing through it)
  // align with the flattened scatter shown in the 'all' scope.
  normType: 'standard' | 'flattened';
  flattenedOffset: number;
}

interface XYPoint {
  x: number;
  y: number;
}

function pairXY(xs: number[] | undefined, ys: number[] | undefined): XYPoint[] {
  if (!xs || !ys) return [];
  const len = Math.min(xs.length, ys.length);
  const out: XYPoint[] = [];
  for (let i = 0; i < len; i++) {
    if (Number.isFinite(xs[i]) && Number.isFinite(ys[i])) {
      out.push({ x: xs[i], y: ys[i] });
    }
  }
  return out;
}

export default function NormalisationSlopePlot({
  plotType,
  dataLevel,
  session,
  plotData,
  configIdx,
  currentRun,
  selectedTraverse,
  configs,
  xRef,
  normType,
  flattenedOffset,
}: Props): ReactElement {
  // Effective y-shift to apply to per-traverse points (and the marker / grey
  // curve that derive from them). Zero unless flattened mode is active and the
  // selected traverse's config has a non-zero offset vs Config A.
  const flattenedYShift =
    normType === 'flattened' && configIdx > 0 ? flattenedOffset : 0;
  // X-axis scale options mirror LinePlot.tsx:21-48. Body-position plots get a
  // separate persistence key per axis (head_body / chest_body) so each remembers
  // its own scale and they don't clobber each other or the yaw/speed scales.
  const scaleConfig = useMemo(() => {
    if (plotType === 'yaw_plot') {
      return {
        key: 'xw',
        options: [
          { value: 6, label: '6' },
          { value: 12, label: '12' },
          { value: 18, label: '18' },
          { value: 30, label: '30' },
          { value: 45, label: '45' },
          { value: 'auto', label: 'Auto' },
        ],
      };
    }
    if (plotType === 'head_plot') {
      return {
        key: 'head_body',
        options: [
          { value: 2, label: '2' },
          { value: 4, label: '4' },
          { value: 6, label: '6' },
          { value: 10, label: '10' },
          { value: 'auto', label: 'Auto' },
        ],
      };
    }
    if (plotType === 'chest_plot') {
      return {
        key: 'chest_body',
        options: [
          { value: 2, label: '2' },
          { value: 4, label: '4' },
          { value: 6, label: '6' },
          { value: 10, label: '10' },
          { value: 'auto', label: 'Auto' },
        ],
      };
    }
    return {
      key: 'speed',
      options: [
        { value: 3, label: '3' },
        { value: 6, label: '6' },
        { value: 9, label: '9' },
        { value: 12, label: '12' },
        { value: 'auto', label: 'Auto' },
      ],
    };
  }, [plotType]);

  const Y_SCALE_OPTIONS = [
    { value: 0.04, label: '0.04' },
    { value: 0.06, label: '0.06' },
    { value: 0.08, label: '0.08' },
    { value: 0.1, label: '0.10' },
    { value: 0.15, label: '0.15' },
    { value: 'auto', label: 'Auto' },
  ];

  const scaleKey = scaleConfig.key;
  const yScaleKey = `${scaleKey}_y`;

  const initialXAxisScale = session?.plotting_scales?.[scaleKey] ?? 'auto';
  const [xAxisScale, setXAxisScale] = useState<number | 'auto'>(initialXAxisScale);
  const initialYAxisScale = session?.plotting_scales?.[yScaleKey] ?? 'auto';
  const [yAxisScale, setYAxisScale] = useState<number | 'auto'>(initialYAxisScale);

  const updateSessionMutation = useUpdateSession(session?.id);
  const setSessionCache = useSetSession();
  const invalidateSession = useInvalidateSession(session?.id);

  const persistScale = (key: string, newScale: number | 'auto') => {
    if (!session?.id) return;
    const current = session.plotting_scales || {};
    const updated = { ...current };
    if (newScale === 'auto') delete updated[key];
    else updated[key] = newScale;
    setSessionCache({ ...session, plotting_scales: updated });
    updateSessionMutation.mutate(
      { plotting_scales: Object.keys(updated).length > 0 ? updated : {} },
      { onSuccess: () => invalidateSession() },
    );
  };

  const handleXAxisScaleChange = (newScale: number | 'auto') => {
    setXAxisScale(newScale);
    persistScale(scaleKey, newScale);
  };
  const handleYAxisScaleChange = (newScale: number | 'auto') => {
    setYAxisScale(newScale);
    persistScale(yScaleKey, newScale);
  };

  // Compute the scatter overlay according to dataLevel.
  // Notes:
  // - 'all': every backend per-config tuple (matches LinePlot's existing rendering).
  // - 'config': just the tuple for the selected run's config.
  // - 'run' / 'traverse': computed client-side from currentRun.traverse using channel keys
  //   (no backend support yet for these narrower scopes).
  const xKey = X_CHANNEL[plotType];
  const yKey = Y_CHANNEL[plotType];

  const { scatterByConfig, scatterRun, scatterTraverse } = useMemo(() => {
    if (!plotData?.configs) return { scatterByConfig: [], scatterRun: [], scatterTraverse: [] };
    // Per-config scatter (the 'all' / 'config' scope dots) is already flattened
    // server-side when normType is 'flattened', so we use the values as shipped.
    const byConfig: { letter: string; data: XYPoint[] }[] = plotData.configs.map(
      (tuple: any, i: number) => {
        const letter = configs?.[i]?.letter ?? `Config ${i}`;
        return { letter, data: pairXY(tuple?.[0], tuple?.[1]) };
      },
    );
    // Per-traverse scatter (run scope and the red traverse marker) uses raw
    // per-traverse arrays; in flattened mode + non-Config-A configs we apply the
    // config offset so the markers line up with the flattened 'all' scatter.
    const runXs: number[] = currentRun?.traverse?.[xKey] ?? [];
    const rawRunYs: number[] = currentRun?.traverse?.[yKey] ?? [];
    const runYs = flattenedYShift !== 0
      ? rawRunYs.map((y) => (Number.isFinite(y) ? y + flattenedYShift : y))
      : rawRunYs;
    const runPoints = pairXY(runXs, runYs);
    const tx = runXs[selectedTraverse];
    const ty = runYs[selectedTraverse];
    const traversePoints =
      Number.isFinite(tx) && Number.isFinite(ty) ? [{ x: tx, y: ty }] : [];
    return {
      scatterByConfig: byConfig,
      scatterRun: runPoints,
      scatterTraverse: traversePoints,
    };
  }, [plotData, configs, currentRun, xKey, yKey, selectedTraverse, flattenedYShift]);

  // Bail out if the backend slope curve isn't available — we can't draw anything useful.
  if (!plotData || !plotData.plot_dict) {
    return (
      <div className="cr-p-4 cr-text-gray-500 cr-italic">
        No slope data available for this plot.
      </div>
    );
  }

  // Backend axis extents (declared early so the speed-line extension below can use it).
  const extents = plotData.extents ?? [
    [0, 1],
    [0, 1],
  ];

  // Build the slope line from parabola + line concatenation (mirrors LinePlot:154-160).
  const lineXs: number[] = (plotData.plot_dict.parabola?.[0] ?? []).concat(
    plotData.plot_dict.line?.[0] ?? [],
  );
  const lineYs: number[] = (plotData.plot_dict.parabola?.[1] ?? []).concat(
    plotData.plot_dict.line?.[1] ?? [],
  );
  let linePoints: [number, number][] = lineXs.map(
    (x: number, i: number) => [x, lineYs[i]],
  );

  // For purely-linear slopes (speed and body-position), extend the line out to a
  // generous upper bound so the curve continues past the reference X even when
  // the X-axis is centred-and-widened around (xRef, traverseX). Linear extrapolation
  // from the last two points preserves the slope; we never extrapolate the yaw
  // parabola because its shape is only meaningful within the backend's domain.
  // Body-position plots use a smaller buffer (cm units, narrower data range).
  const isLinearCentred =
    plotType === 'speed_plot' || plotType === 'head_plot' || plotType === 'chest_plot';
  if (isLinearCentred && linePoints.length >= 2) {
    const last = linePoints[linePoints.length - 1];
    const secondLast = linePoints[linePoints.length - 2];
    const dx = last[0] - secondLast[0];
    const dy = last[1] - secondLast[1];
    const refX = Number.isFinite(xRef) ? xRef : 0;
    const buffer = plotType === 'speed_plot' ? 10 : 5;
    const targetMax = Math.max(extents[0]?.[1] ?? 0, refX + buffer);
    if (dx !== 0 && targetMax > last[0]) {
      const slope = dy / dx;
      const yAtTarget = last[1] + slope * (targetMax - last[0]);
      linePoints = [...linePoints, [targetMax, yAtTarget]];
    }
  }

  // Build the rendered series + matching colors / dashArray / markers arrays.
  // Order: scatter(s) first, then the slope line.
  const series: any[] = [];
  const colors: string[] = [];
  const dashArray: number[] = [];
  const markerSizes: number[] = [];

  if (dataLevel === 'all') {
    let effectiveIndex = 0;
    scatterByConfig.forEach(({ letter, data }) => {
      if (data.length === 0) return;
      series.push({ name: letter, type: 'scatter', data: data.map((p) => [p.x, p.y]) });
      colors.push(PALETTE[effectiveIndex % PALETTE.length]);
      dashArray.push(0);
      markerSizes.push(3);
      effectiveIndex++;
    });
  } else if (dataLevel === 'config') {
    const tuple = scatterByConfig[configIdx];
    if (tuple && tuple.data.length > 0) {
      series.push({
        name: tuple.letter,
        type: 'scatter',
        data: tuple.data.map((p) => [p.x, p.y]),
      });
      colors.push(PALETTE[Math.max(0, configIdx) % PALETTE.length]);
      dashArray.push(0);
      markerSizes.push(3);
    }
  } else if (dataLevel === 'run') {
    if (scatterRun.length > 0) {
      const runLabel = currentRun?.run_number != null ? `Run ${currentRun.run_number}` : 'Run';
      series.push({
        name: runLabel,
        type: 'scatter',
        data: scatterRun.map((p) => [p.x, p.y]),
      });
      colors.push(PALETTE[Math.max(0, configIdx) % PALETTE.length]);
      dashArray.push(0);
      markerSizes.push(3);
    }
  }
  // Note: in 'traverse' scope no scope-scatter is added here because the red
  // traverse marker pushed at the very end of this build (below the reference
  // marker) already represents the single traverse point — and we want that
  // marker to render on top regardless of scope.

  // Always append the slope line last so it renders on top.
  series.push({ name: 'Normalisation Slope', type: 'line', data: linePoints });
  colors.push(LINE_COLOR);
  dashArray.push(5);
  markerSizes.push(0);

  // Helper: linearly interpolate the slope curve at a given x. Slope is monotonic
  // in x (parabola + line concatenation, plotted left-to-right), so one forward
  // scan finds the bracketing pair; falls back to the nearest endpoint if x is
  // outside the curve's domain.
  const sampleCurveAt = (x: number): number | null => {
    if (!Number.isFinite(x) || linePoints.length < 2) return null;
    for (let i = 0; i < linePoints.length - 1; i++) {
      const [x0, y0] = linePoints[i];
      const [x1, y1] = linePoints[i + 1];
      if ((x0 <= x && x <= x1) || (x1 <= x && x <= x0)) {
        const span = x1 - x0;
        return span === 0 ? y0 : y0 + (y1 - y0) * ((x - x0) / span);
      }
    }
    const first = linePoints[0];
    const last = linePoints[linePoints.length - 1];
    return Math.abs(x - first[0]) < Math.abs(x - last[0]) ? first[1] : last[1];
  };

  // ΔCdA shown in the text label: the per-traverse channel-formula difference
  // (CdAN_xG3 minus the axis-specific partially-normalised variant). This is the
  // *displayed* number; the curve geometry below is independently driven by the
  // empirical traverse point so the grey curve always passes through the red
  // traverse marker, even when the channel delta differs from the geometric gap.
  const minuendArr: number[] = currentRun?.traverse?.[DELTA_MINUEND] ?? [];
  const subtrahendArr: number[] =
    currentRun?.traverse?.[DELTA_SUBTRAHEND[plotType]] ?? [];
  const minuendVal = minuendArr[selectedTraverse];
  const subtrahendVal = subtrahendArr[selectedTraverse];
  const channelDelta =
    Number.isFinite(minuendVal) && Number.isFinite(subtrahendVal)
      ? minuendVal - subtrahendVal
      : null;

  // Reference + traverse-curve geometry. The grey parallel curve passes through
  // the empirical traverse point (geometric offset = traverse_y minus slope_at_tx),
  // and the vertical connector spans the same offset at xRef so it visually
  // matches the curve's vertical gap from the reference slope.
  const yRef = Number.isFinite(xRef) ? sampleCurveAt(xRef) : null;
  let traverseCurvePoints: [number, number][] | null = null;
  let connectorPoints: [number, number][] | null = null;
  let dCdA: number | null = null;
  let dCdAMidY: number | null = null;
  if (
    scatterTraverse.length > 0
    && linePoints.length >= 2
    && Number.isFinite(scatterTraverse[0].x)
    && Number.isFinite(scatterTraverse[0].y)
  ) {
    const tx = scatterTraverse[0].x;
    const ty = scatterTraverse[0].y;
    const yCurveAtTx = sampleCurveAt(tx);
    if (yCurveAtTx !== null) {
      const geomOffset = ty - yCurveAtTx;
      traverseCurvePoints = linePoints.map(([x, y]) => [x, y + geomOffset]);
      if (yRef !== null) {
        const yTraverseAtRef = yRef + geomOffset;
        connectorPoints = [
          [xRef, yRef],
          [xRef, yTraverseAtRef],
        ];
        dCdAMidY = (yRef + yTraverseAtRef) / 2;
        // Display the channel-formula ΔCdA in the text label (not the geometric
        // offset). When the model fits well, the two are close; divergence is
        // diagnostic.
        dCdA = channelDelta;
      }
    }
  }

  // Grey "traverse curve": same shape as the reference slope, vertically offset
  // so it passes through the selected traverse point.
  if (traverseCurvePoints) {
    series.push({
      name: 'Traverse curve',
      type: 'line',
      data: traverseCurvePoints,
    });
    colors.push('#9CA3AF');  // tailwind gray-400
    dashArray.push(5);
    markerSizes.push(0);
  }

  // Vertical grey connector at xRef between the reference and traverse curves.
  if (connectorPoints) {
    series.push({
      name: 'Reference → traverse curve',
      type: 'line',
      data: connectorPoints,
    });
    colors.push('#9CA3AF');
    dashArray.push(5);
    markerSizes.push(0);
  }

  // Black reference marker on top of the connector.
  if (yRef !== null) {
    series.push({
      name: 'Reference',
      type: 'scatter',
      data: [[xRef, yRef]],
    });
    colors.push('#000000');
    dashArray.push(0);
    markerSizes.push(6);
  }

  // Red traverse marker drawn LAST so it sits on top of every other element
  // (scope scatter, slope line, traverse curve, connector, reference marker).
  // Rendered in every scope so the user can always see where the selected
  // traverse sits, not just when in 'traverse' scope.
  if (scatterTraverse.length > 0) {
    series.push({
      name: `Traverse ${selectedTraverse + 1}`,
      type: 'scatter',
      data: scatterTraverse.map((p) => [p.x, p.y]),
    });
    colors.push('#FF4560');
    dashArray.push(0);
    markerSizes.push(5);
  }

  let yMin = extents[1][0];
  let yMax = extents[1][1];
  if (yAxisScale !== 'auto') {
    const half = (yAxisScale as number) / 2;
    const centre = (extents[1][0] + extents[1][1]) / 2;
    yMin = centre - half;
    yMax = centre + half;
  }
  // X-axis range. Yaw starts at 0 by convention (zero crosswind is a meaningful
  // anchor); speed and body-position plots centre on the midpoint of the
  // reference and traverse points so neither sits at the chart edge — width is
  // the chosen scale, or the backend's natural extent width when 'auto'.
  // Defensive: if xRef sits well outside the backend extents (likely a unit
  // mismatch — e.g. ref in mm but axis in cm), ignore xRef and centre on the
  // backend extents midpoint so the data points still appear on-chart.
  let xMin = extents[0][0];
  let xMax = extents[0][1];
  if (isLinearCentred) {
    const extWidth = Math.max(extents[0][1] - extents[0][0], 1e-9);
    const extMid = (extents[0][0] + extents[0][1]) / 2;
    const xRefSane = Number.isFinite(xRef) && Math.abs(xRef - extMid) < extWidth * 5;
    let centre = xRefSane ? xRef : extMid;
    if (
      xRefSane
      && scatterTraverse.length > 0
      && Number.isFinite(scatterTraverse[0].x)
    ) {
      centre = (xRef + scatterTraverse[0].x) / 2;
    }
    const width = xAxisScale === 'auto' ? extWidth : (xAxisScale as number);
    xMin = centre - width / 2;
    xMax = centre + width / 2;
  } else if (xAxisScale !== 'auto') {
    xMin = 0;
    xMax = xAxisScale as number;
  }

  const props = plotData.properties ?? {};
  const titleText = props.suptitle && props.title
    ? `${props.suptitle}: ${props.title}`
    : props.title ?? '';
  const lineType = props.plot_note?.[0] ?? '';
  const notes: string[] = (props.plot_note?.[1] ?? '').split('\n').filter((n: string) => n);

  const options: ApexOptions = {
    // offsetY shifts the title up so it sits clear of the pan/zoom toolbar that
    // would otherwise overlap on the right side.
    title: {
      text: titleText,
      align: 'left',
      offsetY: -5,
      style: { fontSize: '13px' },
    },
    chart: {
      height: 350,
      type: 'line',
      animations: { enabled: false },
      // Pan / zoom toolbar. zoom.enabled is required for the pan tool to work in
      // ApexCharts — they share the same interaction layer. autoScaleYaxis keeps
      // the y-axis sensible when the user pans across regions of differing data.
      zoom: { enabled: true, type: 'xy', autoScaleYaxis: true },
      toolbar: {
        show: true,
        offsetY: 15,
        tools: {
          download: false,
          selection: false,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
    },
    tooltip: { enabled: false },
    yaxis: {
      tickAmount: 7,
      min: yMin,
      max: yMax,
      labels: { formatter: (val: number) => val.toFixed(3) },
    },
    xaxis: {
      tickAmount: 15,
      min: xMin,
      max: xMax,
      labels: { formatter: (val: string) => parseFloat(val).toFixed(1) },
    },
    annotations: {
      yaxis: [
        {
          y: extents[1][1] - 0.05 * (extents[1][1] - extents[1][0]),
          width: '0%',
          label: {
            offsetX: 10,
            position: 'left',
            textAnchor: 'start',
            borderColor: LINE_COLOR,
            style: { color: '#fff', background: LINE_COLOR },
            text: lineType,
          },
        },
      ].concat(
        notes.map((note: string, index: number) => ({
          y: extents[1][0] + (index + 1) * 0.08 * (extents[1][1] - extents[1][0]),
          width: '0%',
          label: {
            offsetX: 10,
            position: 'left',
            textAnchor: 'start',
            borderColor: LINE_COLOR,
            style: { color: '#fff', background: LINE_COLOR },
            text: note,
          },
        })),
      ),
      // ΔCdA label sitting next to the vertical connector. Marker is hidden;
      // text is offset to the right so it doesn't overlap the dashed line.
      // No background — plain grey text floating on the plot area.
      points: dCdA !== null && dCdAMidY !== null
        ? [
          {
            x: xRef,
            y: dCdAMidY,
            marker: { size: 0 },
            label: {
              text: `ΔCdA = ${dCdA >= 0 ? '+' : ''}${dCdA.toFixed(4)}`,
              borderColor: 'transparent',
              offsetX: 8,
              offsetY: 0,
              textAnchor: 'start',
              style: {
                color: '#374151',  // tailwind gray-700
                background: 'transparent',
                fontSize: '11px',
              },
            },
          },
        ]
        : [],
    },
    stroke: { width: 5, curve: 'straight', dashArray: dashArray },
    fill: { type: 'solid' },
    markers: { size: markerSizes },
    colors: colors,
  };

  // Empty-state hints for config / run / traverse scopes when no data is available.
  let scopeHint: string | null = null;
  if (dataLevel === 'config' && (configIdx < 0 || !scatterByConfig[configIdx])) {
    scopeHint = 'No per-config aggregate available for this run.';
  } else if (dataLevel === 'run' && scatterRun.length === 0) {
    scopeHint = 'No per-traverse data available for this run.';
  } else if (dataLevel === 'traverse' && scatterTraverse.length === 0) {
    scopeHint = `No data for traverse ${selectedTraverse + 1}.`;
  }

  return (
    <div>
      <Chart options={options} series={series} type="line" width="100%" height="380" />
      {scopeHint && (
        <div className="cr-text-xs cr-text-amber-700 cr-italic cr-mt-1">{scopeHint}</div>
      )}
      <div className="cr-mt-2 cr-flex cr-items-center cr-gap-4 cr-flex-wrap">
        <div className="cr-flex cr-items-center cr-gap-2">
          <span className="cr-font-medium cr-text-gray-700">X-Axis Scale:</span>
          <select
            value={xAxisScale}
            onChange={(e) => {
              const newValue = e.target.value === 'auto' ? 'auto' : parseFloat(e.target.value);
              handleXAxisScaleChange(newValue);
            }}
            className="cr-cursor-pointer cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1"
          >
            {scaleConfig.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="cr-flex cr-items-center cr-gap-2">
          <span className="cr-font-medium cr-text-gray-700">Y-Axis Scale:</span>
          <select
            value={yAxisScale}
            onChange={(e) => {
              const newValue = e.target.value === 'auto' ? 'auto' : parseFloat(e.target.value);
              handleYAxisScaleChange(newValue);
            }}
            className="cr-cursor-pointer cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1"
          >
            {Y_SCALE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
